import { Slot } from '@radix-ui/react-slot'
import React, { forwardRef, useContext, useLayoutEffect } from 'react'

import { mergeRefs } from 'utils/ref_utils'

import { PopperContext } from './context'

interface PopperAnchorProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  /**
   * Custom components can forward refs to any node, which may or may not be
   * the node that you want to serve as the anchor for the popper. For example,
   * the ref may be forwarded to an `<input>` element but you want the input's
   * wrapping element to be the anchor. If this is the case, use
   * `getAnchorRelativeToRef` to find the node you're looking for.
   */
  getAnchorRelativeToRef?: (node: HTMLElement | null) => HTMLElement | null | undefined;
  virtualAnchor?: DOMRect | null;
}

export const PopperAnchor = forwardRef<HTMLElement, PopperAnchorProps>(
  ({ asChild, getAnchorRelativeToRef, virtualAnchor, ...props }, forwardedRef) => {
    const { placement, setAnchor } = useContext(PopperContext)

    useLayoutEffect(() => {
      if (virtualAnchor) {
        setAnchor({
          getBoundingClientRect: () => virtualAnchor,
        })
      } else {
        setAnchor(null)
      }
    }, [virtualAnchor])

    if (virtualAnchor) return null

    const Component = asChild ? Slot : 'div'
    const [side, align = 'center'] = placement
      ? placement.split('-')
      : [undefined, undefined]

    function ref (node: HTMLElement | null) {
      setAnchor(getAnchorRelativeToRef?.(node) || node)
    }

    return (
      <Component
        {...props}
        data-popper-align={align}
        data-popper-side={side}
        ref={mergeRefs(ref, forwardedRef)}
      />
    )
  }
)
